<script lang="ts">
	/**
	 * The `/we-moved` page. PageRouter redirects here from the legacy
	 * `myluxedo.com` hosts: signing in there creates a session scoped to the old
	 * domain (cookies don't cross registrable domains), so we point people at the
	 * canonical portal instead of letting them log in.
	 */
	import LuxedoLogo from "../../../assets/icons/LuxedoLogo.svg"
	import { CANONICAL_PORTAL_URL } from "../../../modules/legacy-domain"
	import { NEXT_ROUTE_KEY } from "../Routes"

	// Carry the path the user was originally after. This renders at /we-moved, so
	// `location.pathname` is useless here — PageRouter captures the real target
	// before redirecting and forwards it as `next`, so read that. Relative paths
	// only: an absolute/protocol-relative value would let a crafted legacy-domain
	// link bounce someone to an arbitrary host off our own "we've moved" page.
	function carriedPath(): string {
		const next = new URLSearchParams(window.location.search).get(NEXT_ROUTE_KEY)
		if (!next || !next.startsWith("/") || next.startsWith("//")) return ""
		return next
	}

	const destination = CANONICAL_PORTAL_URL
		? `${CANONICAL_PORTAL_URL.replace(/\/$/, "")}${carriedPath()}`
		: ""
</script>

<main>
	<img class="logo" src={LuxedoLogo} alt="Luxedo" />

	<div class="content">
		<h1>We've moved</h1>
		<p class="subtitle">
			The Luxedo portal has a new home. Your account, projectors, and shows all came with it —
			nothing has been lost.
		</p>

		{#if destination}
			<p class="subtitle">Sign in at our new address to pick up where you left off.</p>
			<a class="outline-button cta" href={destination}>Go to new portal</a>
		{:else}
			<!-- VITE_PORTAL_URL wasn't set at build time. Say something useful rather
			     than rendering a dead link. -->
			<p class="subtitle">
				Please use our new address to sign in. If you're not sure where that is, our team can point
				you the right way.
			</p>
		{/if}

		<p class="footer">
			Need a hand? Visit our
			<a href="https://luxhome.ai/pages/help" target="_blank" rel="noopener">help desk</a>.
		</p>
	</div>
</main>

<style>
	main {
		padding: 0 2rem 1rem 2rem;
		height: 100dvh;
		overflow-x: hidden;
		overflow-y: auto;
		display: flex;
		flex-direction: column;
		align-items: center;
	}

	.logo {
		height: 2.5rem;
		width: auto;
		margin: 2rem 0 0 0;
	}

	.content {
		flex-grow: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		text-align: center;
		max-width: 34rem;
	}

	h1 {
		font-size: var(--text-h3);
		color: var(--color-main);
		margin: 0 0 1rem 0;
	}

	.subtitle {
		margin: 0 0 0.75rem 0;
		line-height: 1.5;
	}

	.cta {
		margin-top: 1.5rem;
		padding: 0.75rem 1.5rem;
		border-radius: 0.5rem;
		font-weight: 600;
		transition:
			background-color 250ms,
			color 250ms;
	}

	.cta:hover,
	.cta:focus-visible {
		color: var(--color-text-light);
	}

	.footer {
		margin-top: 2.5rem;
		font-size: 0.9em;
	}

	@media only screen and (max-width: 800px) {
		.content {
			justify-content: flex-start;
			padding-top: 3rem;
		}
	}
</style>
